import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_STRING as api } from '@/App';
import { Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useDarkMode } from '@/hooks/useDarkMode';

import { logger } from '@/utils/logger';
import ConfirmModal from '@/components/ConfirmModal';
import ProductCard from '@/components/ProductCard';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Trash2, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import EmptyState from '@/components/EmptyState';
import ShareButton from '@/components/ShareButton';
export default function RecipientDetail({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const darkMode = useDarkMode();
  const [recipient, setRecipient] = useState(null);
  const [savedGifts, setSavedGifts] = useState([]);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    confirmText: 'Delete'
  });

  const fetchData = useCallback(async () => {
    try {
      const headers = { };

      // Fetch recipient details
      const recipientRes = await fetch(`${api}/recipients`, { headers });
      if (recipientRes.ok) {
        const recipients = await recipientRes.json();
        const currentRecipient = recipients.find(r => r.id === id);
        setRecipient(currentRecipient);

        if (currentRecipient) {
          // Fetch saved gifts
          const giftsRes = await fetch(`${api}/recipients/${id}/saved-gifts`, { headers });
          if (giftsRes.ok) {
            const giftsData = await giftsRes.json();
            setSavedGifts(giftsData);
          }

          // Check if there's an associated questionnaire
          const questionnairesRes = await fetch(`${api}/keepsakes`, { headers });
          if (questionnairesRes.ok) {
            const questionnaires = await questionnairesRes.json();
            // Find questionnaire matching this recipient's name
            const matchingQ = questionnaires.find(q => 
              q.recipient_name.toLowerCase() === currentRecipient.name.toLowerCase()
            );
            setQuestionnaire(matchingQ);
          }
        }
      }
    } catch (error) {
      logger.error('Failed to fetch data:', error);
      toast.error('Failed to load recipient details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [user, id, navigate, fetchData]);

  const handleDeleteGift = async (savedGiftId) => {
    setConfirmModal({
      isOpen: true,
      title: 'Remove Gift?',
      message: 'This will remove this gift from your saved items for this recipient.',
      confirmText: 'Remove',
      onConfirm: async () => {
        try {
          const response = await fetch(`${api}/saved-gifts/${savedGiftId}`, {
            method: 'DELETE',
          });
          
          if (response.ok) {
            toast.success('Gift removed');
            fetchData();
          }
        } catch (error) {
          toast.error('Failed to remove gift');
        }
      }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9E6752]"></div>
      </div>
    );
  }

  if (!recipient) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl mb-4" style={{ color: '#73766A' }}>Recipient not found</p>
          <Link to="/keepsakes">
            <Button>Back to Keepsakes</Button>
          </Link>
        </div>
      </div>
    );
  }

  const aiSummary = recipient.questionnaire_responses?.interests 
    ? `${recipient.name} says they like ${Array.isArray(recipient.questionnaire_responses.interests) ? recipient.questionnaire_responses.interests.join(', ') : recipient.questionnaire_responses.interests}`
    : null;

  return (
    <div className="min-h-screen page-enter" style={{ background: darkMode.bgPrimary }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back button */}
        <button
          onClick={() => navigate('/keepsakes')}
          className="flex items-center gap-2 mb-6 text-sm hover:underline"
          style={{ color: '#9E6752' }}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Keepsakes
        </button>

        {/* Header with gift box accent */}
        <div className="mb-6 flex items-center gap-4">
          <div 
            className="w-16 h-16 rounded-xl flex items-center justify-center shadow-lg"
            style={{ background: `linear-gradient(135deg, ${recipient.color} 0%, ${recipient.color}dd 100%)`, border: '2px solid #FED7A5' }}
          >
            <span className="text-2xl">🎁</span>
          </div>
          <div>
            <h1 className="text-3xl font-bold" style={{ color: '#20212B' }}>{recipient.name}</h1>
            <p className="text-base" style={{ color: '#73766A' }}>
              {recipient.relationship}{recipient.occasion && ` • ${recipient.occasion}`}
            </p>
          </div>
        </div>

        {/* AI Questionnaire Section */}
        {questionnaire && (
          <div className="bg-white rounded-xl p-6 mb-6" style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold mb-1" style={{ color: '#20212B' }}>AI Questionnaire</h2>
                <p className="text-sm" style={{ color: '#73766A' }}>Get personalized gift suggestions from friends</p>
              </div>
              {questionnaire.status === 'completed' ? (
                <Link to={`/keepsakes/${questionnaire.id}/results`}>
                  <Button size="sm" style={{ background: '#2D4354', color: 'white' }}>
                    <Sparkles className="w-4 h-4 mr-1" />
                    View AI Results
                  </Button>
                </Link>
              ) : (
                <Link to={`/keepsakes/${questionnaire.id}`}>
                  <Button size="sm" variant="outline">
                    <Users className="w-4 h-4 mr-1" />
                    {questionnaire.response_count || 0} Responses
                  </Button>
                </Link>
              )}
            </div>
            
            {aiSummary && (
              <div className="p-4 rounded-lg" style={{ background: darkMode.bgPrimary }}>
                <p className="text-sm italic" style={{ color: '#534145' }}>
                  &ldquo;{aiSummary}&rdquo;
                </p>
              </div>
            )}

            {!questionnaire.status || questionnaire.status === 'active' ? (
              <div className="mt-4 flex items-center gap-2">
                <ShareButton
                  shareUrl={`/gift-questionnaire/${questionnaire.share_id}`}
                  title={`Help me find a gift for ${recipient.name}!`}
                  description={`Share your thoughts about ${recipient.name} to help me pick the perfect gift.`}
                />
                <p className="text-xs" style={{ color: '#73766A' }}>Share with friends to get more insights</p>
              </div>
            ) : null}
          </div>
        )}

        {!questionnaire && (
          <div className="bg-gradient-to-r from-[#2D4354] to-[#534145] rounded-xl p-6 mb-6 text-white">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-lg font-bold mb-1">Want AI-Powered Gift Suggestions?</h2>
                <p className="text-sm opacity-90">Create a questionnaire and get personalized recommendations</p>
              </div>
              <Link to="/keepsakes/create">
                <Button size="sm" style={{ background: '#FED7A5', color: '#20212B' }}>
                  <Sparkles className="w-4 h-4 mr-1" />
                  Create Questionnaire
                </Button>
              </Link>
            </div>
          </div>
        )}

        {/* Saved Gifts */}
        <div className="bg-white rounded-xl p-6" style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
          <h2 className="text-lg font-bold mb-4" style={{ color: '#20212B' }}>Saved Gift Ideas ({savedGifts.length})</h2>
          {savedGifts.length === 0 ? (
            <EmptyState
              icon={Sparkles}
              title="No gifts saved yet"
              description='Browse the catalog and tap "Add to Keepsakes" to start collecting ideas for this person.'
              cta={{
                label: 'Browse Gifts',
                href: '/browse',
                testId: 'recipient-empty-browse',
              }}
              compact
              testId="recipient-saved-gifts-empty"
            />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {savedGifts.map(({ saved_gift, product }) => (
                <div key={saved_gift.id} className="relative">
                  <ProductCard product={product} user={user} />
                  <button
                    onClick={() => handleDeleteGift(saved_gift.id)}
                    className="absolute top-2 right-2 p-2 rounded-full bg-white shadow-md hover:bg-red-50 text-red-600 transition-colors z-10"
                    title="Remove from saved gifts"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
      />
    </div>
  );
}